import React, { useEffect, useState } from 'react';
import productService from '../../services/api/productService';
import type { Product, ProductExtendedMetadata } from '../../types/product';

interface Props {
  productId: number;
  onGenerate: () => Promise<void>;
  onSkip: () => void;
}
const HEADER = 'flex items-start justify-between gap-4 mb-5';
const TITLE = 'text-[1.45rem] text-gray-800 m-0 mb-1.5';
const SUB = 'text-[0.88rem] text-gray-500 m-0 leading-[1.6]';
const EDIT_BUTTON =
  'min-h-10 py-0 px-4 border border-[#d5d8e5] rounded-lg bg-white text-gray-600 font-bold cursor-pointer';
const SECTION = 'border border-gray-200 rounded-[14px] p-5 mb-4 bg-white';
const LABEL =
  'block text-[0.75rem] font-extrabold text-[#7a8095] tracking-[0.05em] mb-[7px]';
const VALUE =
  'text-[0.95rem] text-[#303548] leading-[1.7] whitespace-pre-wrap m-0 mb-4 last:mb-0';
const INPUT =
  'w-full border border-[#cfd3df] rounded-lg py-[11px] px-3 [font:inherit] mb-[14px]';
const TEXTAREA =
  'w-full min-h-[92px] border border-[#cfd3df] rounded-lg py-[11px] px-3 [font:inherit] resize-y mb-[14px]';
const SEGMENTS = 'grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3';
const SEGMENT =
  'p-[15px] rounded-[11px] bg-[#f7f7fb] border border-[#e7e7f0]';
const SEGMENT_TITLE = 'text-[0.95rem] text-[#303548] m-0 mb-2';
const SEGMENT_COPY = 'text-[0.8rem] text-[#686e82] leading-[1.55] mx-0 my-1';
const SAVE =
  'min-h-[42px] py-0 px-[18px] bg-[#303653] text-white border-0 rounded-lg font-bold cursor-pointer';
const CTA =
  'relative overflow-hidden flex items-center justify-center gap-2.5 ' +
  'w-[min(520px,100%)] min-h-[58px] mx-auto mt-[26px] mb-0 border-0 rounded-[13px] ' +
  'bg-[linear-gradient(115deg,#4f5db5,#7c3aed)] text-white text-[1.02rem] font-extrabold cursor-pointer ' +
  'animate-[sob-cta-pulse_2.2s_ease-in-out_infinite] ' +
  "after:content-[''] after:absolute after:top-[-20%] after:right-auto after:bottom-[-20%] after:left-0 after:w-[32%] " +
  'after:bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.42),transparent)] ' +
  'after:translate-x-[-140%] after:animate-[sob-sweep_2.8s_ease-in-out_infinite] ' +
  'disabled:opacity-[0.65] disabled:cursor-wait disabled:animate-none ' +
  'motion-reduce:animate-none motion-reduce:after:animate-none';
const SKIP =
  'block mx-auto mt-4 mb-0 border-0 bg-transparent bg-none text-[#8a8fa3] underline cursor-pointer';
const STATE = 'text-center py-[60px] px-0 text-gray-500';

const SignupProductReviewStep: React.FC<Props> = ({
  productId,
  onGenerate,
  onSkip,
}) => {
  const [product, setProduct] = useState<Product | null>(null);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [metadata, setMetadata] = useState<ProductExtendedMetadata>({});
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    productService
      .getKnowledgeProductById(productId)
      .then((r) => {
        setProduct(r.data);
        setName(r.data.name);
        setDescription(r.data.description || '');
        setMetadata(r.data.extended_metadata || {});
      })
      .catch(() => setError('商品情報を読み込めませんでした。'));
  }, [productId]);
  const save = async () => {
    if (!product) return;
    setSaving(true);
    setError('');
    try {
      const r = await productService.updateKnowledgeProduct(productId, {
        name: name.trim() || product.name,
        description,
        extended_metadata: metadata,
      });
      setProduct(r.data);
      setEditing(false);
    } catch {
      setError('商品情報を保存できませんでした。');
    } finally {
      setSaving(false);
    }
  };
  const updateSegment = (
    index: number,
    field: 'name' | 'pain_emotion' | 'ideal_state',
    value: string,
  ) => {
    const segments = [...(metadata.segments || [])];
    const current = segments[index];
    if (!current) return;
    segments[index] =
      field === 'name'
        ? { ...current, name: value }
        : { ...current, insights: { ...current.insights, [field]: value } };
    setMetadata({ ...metadata, segments });
  };
  const generate = async () => {
    setGenerating(true);
    setError('');
    try {
      await onGenerate();
    } catch {
      setError('バナー生成を開始できませんでした。もう一度お試しください。');
      setGenerating(false);
    }
  };
  if (error && !product)
    return (
      <div className={STATE} role="alert">
        {error}
      </div>
    );
  if (!product)
    return <div className={STATE}>商品情報を読み込んでいます...</div>;
  const info = metadata.product || {};
  const segments = metadata.segments || [];
  return (
    <div>
      <div className={HEADER}>
        <div>
          <h2 className={TITLE}>商品情報を確認してください</h2>
          <p className={SUB}>
            AIがURLから整理した内容です。生成前に編集できます。
          </p>
        </div>
        <button
          className={EDIT_BUTTON}
          type="button"
          onClick={() => setEditing((v) => !v)}
        >
          {editing ? '編集をやめる' : '編集する'}
        </button>
      </div>
      {error && (
        <p role="alert" style={{ color: '#dc2626' }}>
          {error}
        </p>
      )}
      <section className={SECTION}>
        <label className={LABEL}>商品・サービス名</label>
        {editing ? (
          <input
            className={INPUT}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        ) : (
          <p className={VALUE}>{product.name}</p>
        )}
        <label className={LABEL}>概要</label>
        {editing ? (
          <textarea
            className={TEXTAREA}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        ) : (
          <p className={VALUE}>
            {product.description || info.description || '情報を取得中です'}
          </p>
        )}
        <label className={LABEL}>USP・強み</label>
        <p className={VALUE}>
          {info.usp || metadata.positioning?.statement_lead || '未設定'}
        </p>
        {editing && (
          <button
            className={SAVE}
            type="button"
            onClick={save}
            disabled={saving}
          >
            {saving ? '保存中...' : '変更を保存'}
          </button>
        )}
      </section>
      <section className={SECTION}>
        <label className={LABEL}>想定セグメント</label>
        <div className={SEGMENTS}>
          {segments.length ? (
            segments.map((segment, index) => (
              <div className={SEGMENT} key={segment.id || index}>
                {editing ? (
                  <>
                    <input
                      className={INPUT}
                      aria-label={`セグメント${index + 1}の名前`}
                      value={segment.name || ''}
                      onChange={(e) =>
                        updateSegment(index, 'name', e.target.value)
                      }
                    />
                    <textarea
                      className={TEXTAREA}
                      aria-label={`セグメント${index + 1}の悩み`}
                      value={segment.insights?.pain_emotion || ''}
                      placeholder="顧客の悩み・課題"
                      onChange={(e) =>
                        updateSegment(index, 'pain_emotion', e.target.value)
                      }
                    />
                    <textarea
                      className={TEXTAREA}
                      aria-label={`セグメント${index + 1}の理想`}
                      value={segment.insights?.ideal_state || ''}
                      placeholder="顧客の理想状態"
                      onChange={(e) =>
                        updateSegment(index, 'ideal_state', e.target.value)
                      }
                    />
                  </>
                ) : (
                  <>
                    <h3 className={SEGMENT_TITLE}>
                      {segment.name || `セグメント${index + 1}`}
                    </h3>
                    <p className={SEGMENT_COPY}>
                      {[segment.age_range, segment.occupation, segment.region]
                        .filter(Boolean)
                        .join('・') || '属性を分析中'}
                    </p>
                    <p className={SEGMENT_COPY}>
                      {segment.insights?.pain_emotion ||
                        segment.insights?.ideal_state ||
                        'インサイトを分析中'}
                    </p>
                  </>
                )}
              </div>
            ))
          ) : (
            <div className={SEGMENT}>
              <h3 className={SEGMENT_TITLE}>セグメントを分析中</h3>
            </div>
          )}
        </div>
        {editing && segments.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <button
              className={SAVE}
              type="button"
              onClick={save}
              disabled={saving}
            >
              {saving ? '保存中...' : 'セグメントを保存'}
            </button>
          </div>
        )}
      </section>
      <button
        className={CTA}
        type="button"
        onClick={generate}
        disabled={generating}
      >
        {generating
          ? '10枚の生成を準備しています...'
          : '✦ 無料でバナーを10枚作る'}
      </button>
      <button className={SKIP} type="button" onClick={onSkip}>
        スキップ
      </button>
    </div>
  );
};
export default SignupProductReviewStep;
